import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { gradientTextSx } from "./Player";
import LifelineButton from "./LifelineButton";
import { LooksTwoRounded, Phone, SportsBar } from "@mui/icons-material";
import {
  getTeamScoresDB,
  isCallAFriendActiveDB,
  isDoubleSipActiveDB,
  isDoubleUpActiveDB,
  isLifelineUsedDB,
} from "../utils/utils";
import { Tables } from "../../database.types";
import { motion, AnimatePresence } from "framer-motion";

interface ScoreboardProps {
  team1: Tables<"teams">;
  team2: Tables<"teams">;
  gameState: Tables<"game_state">;
  rounds: Tables<"rounds">[];
  questions: Tables<"questions">[];
  lifelines: Tables<"round_lifelines">[];
  bonusRounds: Tables<"bonus_rounds">[];
  classicBonusAnswers: Tables<"classic_bonus_answers">[];
  classicBonusTeamResponses: Tables<"classic_bonus_team_responses">[];
  closestGuessQuestions: Tables<"closest_guess_questions">[];
  team1LogoUrl: string | null;
  team2LogoUrl: string | null;
}

// Using motion components to enable animations
const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionPaper = motion(Paper);

const Scoreboard = (props: ScoreboardProps) => {
  const { gameState, rounds, questions, lifelines, bonusRounds, classicBonusAnswers, classicBonusTeamResponses, closestGuessQuestions, team1, team2, team1LogoUrl, team2LogoUrl } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const teamScores: { team1: number; team2: number } = getTeamScoresDB(
    rounds,
    questions,
    lifelines,
    bonusRounds,
    classicBonusAnswers,
    classicBonusTeamResponses,
    closestGuessQuestions,
    team1.id,
    team2.id
  );

  const currentRound = gameState.current_phase === "standard"
    ? rounds.find(r => r.round_number === gameState.current_round_number)
    : null;
  const currentRoundLifelines = currentRound
    ? lifelines.filter(l => l.round_id === currentRound.id)
    : [];

  const team1LifelineState = {
    phoneAFriend: {
      isActive: isCallAFriendActiveDB(currentRoundLifelines, team1.id),
      isUsed: isLifelineUsedDB(lifelines, team1.id, "Phone a Friend"),
    },
    doubleSip: {
      isActive: isDoubleSipActiveDB(currentRoundLifelines, team1.id),
      isUsed: isLifelineUsedDB(lifelines, team1.id, "Double Sip"),
    },
    doubleUp: {
      isActive: isDoubleUpActiveDB(currentRoundLifelines, team1.id),
      isUsed: isLifelineUsedDB(lifelines, team1.id, "Double up"),
    },
  };
  const team2LifelineState = {
    phoneAFriend: {
      isActive: isCallAFriendActiveDB(currentRoundLifelines, team2.id),
      isUsed: isLifelineUsedDB(lifelines, team2.id, "Phone a Friend"),
    },
    doubleSip: {
      isActive: isDoubleSipActiveDB(currentRoundLifelines, team2.id),
      isUsed: isLifelineUsedDB(lifelines, team2.id, "Double Sip"),
    },
    doubleUp: {
      isActive: isDoubleUpActiveDB(currentRoundLifelines, team2.id),
      isUsed: isLifelineUsedDB(lifelines, team2.id, "Double up"),
    },
  };

  // Animation variants for score changes
  const scoreVariants = {
    initial: { scale: 1 },
    animate: {
      scale: [1, 1.3, 1],
      transition: { duration: 0.5 },
    },
  };

  // Animation variants for the entire scoreboard on mount
  const scoreboardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  // Animation variants for team sections
  const teamVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
  };

  // Animation for lifeline buttons
  const lifelineVariants = {
    active: {
      scale: [1, 1.2, 1],
      transition: { duration: 0.4 },
    },
    used: {
      opacity: 0.5,
      scale: 0.95,
      transition: { duration: 0.3 },
    },
  };

  return (
    <MotionPaper
      initial="hidden"
      animate="visible"
      variants={scoreboardVariants}
      sx={{
        width: "100%",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        marginTop: "auto",
        border: "4px solid " + theme.palette.primary.light,
        backgroundColor: theme.palette.primary.dark,
      }}
    >
      {/* Team 1 */}
      <MotionBox
        variants={teamVariants}
        sx={{
          flex: 1,
          borderRight: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
          borderBottom: isMobile
            ? `1px solid ${theme.palette.divider}`
            : "none",
          display: "flex",
          paddingRight: 4,
          flexDirection: "column",
          p: { xs: 1, sm: 1.5 },
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "4px",
            height: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <MotionTypography
            key={`team1-score-${teamScores.team1}`} // Key changes trigger animation
            variants={scoreVariants}
            initial="initial"
            animate="animate"
            variant={isMobile ? "h6" : "h1"}
            fontWeight="bold"
            sx={{
              bgcolor: "primary.main",
              py: 0.5,
              px: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              minWidth: "40px",
              textAlign: "center",
              ...gradientTextSx,
            }}
          >
            {teamScores.team1}
          </MotionTypography>
          <Typography
            variant={isMobile ? "subtitle1" : "h3"}
            fontWeight="bold"
            color="primary"
            noWrap
            sx={{ maxWidth: "70%", ...gradientTextSx }}
          >
            {team1.name}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "space-between",
            pt: 1,
            mt: "auto",
          }}
        >
          <Box display="flex" gap={1} alignItems="flex-end">
            <AnimatePresence>
              <MotionBox
                key={`team1-doubleSip-${team1LifelineState.doubleSip.isActive}-${team1LifelineState.doubleSip.isUsed}`}
                animate={
                  team1LifelineState.doubleSip.isUsed
                    ? "used"
                    : team1LifelineState.doubleSip.isActive
                    ? "active"
                    : "initial"
                }
                variants={lifelineVariants}
              >
                <LifelineButton
                  isActive={team1LifelineState.doubleSip.isActive}
                  isUsed={team1LifelineState.doubleSip.isUsed}
                  icon={<SportsBar fontSize={isMobile ? "small" : "large"} />}
                />
              </MotionBox>
            </AnimatePresence>
            <AnimatePresence>
              <MotionBox
                key={`team1-doubleUp-${team1LifelineState.doubleUp.isActive}-${team1LifelineState.doubleUp.isUsed}`}
                animate={
                  team1LifelineState.doubleUp.isUsed
                    ? "used"
                    : team1LifelineState.doubleUp.isActive
                    ? "active"
                    : "initial"
                }
                variants={lifelineVariants}
              >
                <LifelineButton
                  isActive={team1LifelineState.doubleUp.isActive}
                  isUsed={team1LifelineState.doubleUp.isUsed}
                  icon={
                    <LooksTwoRounded fontSize={isMobile ? "small" : "large"} />
                  }
                />
              </MotionBox>
            </AnimatePresence>
            <AnimatePresence>
              <MotionBox
                key={`team1-phoneAFriend-${team1LifelineState.phoneAFriend.isActive}-${team1LifelineState.phoneAFriend.isUsed}`}
                animate={
                  team1LifelineState.phoneAFriend.isUsed
                    ? "used"
                    : team1LifelineState.phoneAFriend.isActive
                    ? "active"
                    : "initial"
                }
                variants={lifelineVariants}
              >
                <LifelineButton
                  isActive={team1LifelineState.phoneAFriend.isActive}
                  isUsed={team1LifelineState.phoneAFriend.isUsed}
                  icon={<Phone fontSize={isMobile ? "small" : "large"} />}
                />
              </MotionBox>
            </AnimatePresence>
          </Box>

          <motion.img
            src={team1LogoUrl ?? ""}
            alt="Team1Logo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            style={{
              objectFit: "cover",
              height: "auto",
              maxHeight: "80px",
            }}
          />
        </Box>
      </MotionBox>

      <Box
        sx={{
          bgcolor: theme.palette.primary.light,
          width: "8px",
          height: "100%",
        }}
      />

      {/* Team 2 */}
      <MotionBox
        variants={teamVariants}
        sx={{
          flex: 1,
          borderRight: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
          borderBottom: isMobile
            ? `1px solid ${theme.palette.divider}`
            : "none",
          display: "flex",
          paddingRight: 4,
          flexDirection: "column",
          p: { xs: 1, sm: 1.5 },
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "4px",
            height: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h3"}
            fontWeight="bold"
            color="primary"
            noWrap
            sx={{ maxWidth: "70%", ...gradientTextSx }}
          >
            {team2.name}
          </Typography>

          <MotionTypography
            key={`team2-score-${teamScores.team2}`} // Key changes trigger animation
            variants={scoreVariants}
            initial="initial"
            animate="animate"
            variant={isMobile ? "h6" : "h1"}
            fontWeight="bold"
            sx={{
              bgcolor: "primary.main",
              py: 0.5,
              px: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              minWidth: "40px",
              textAlign: "center",
              ...gradientTextSx,
            }}
          >
            {teamScores.team2}
          </MotionTypography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "space-between",
            pt: 1,
            mt: "auto",
          }}
        >
          <motion.img
            src={team2LogoUrl ?? ""}
            alt="Team2Logo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            style={{
              objectFit: "cover",
              height: "auto",
              maxHeight: "80px",
            }}
          />

          <Box display="flex" gap={1} alignItems="flex-end">
            <AnimatePresence>
              <MotionBox
                key={`team2-doubleSip-${team2LifelineState.doubleSip.isActive}-${team2LifelineState.doubleSip.isUsed}`}
                animate={
                  team2LifelineState.doubleSip.isUsed
                    ? "used"
                    : team2LifelineState.doubleSip.isActive
                    ? "active"
                    : "initial"
                }
                variants={lifelineVariants}
              >
                <LifelineButton
                  isActive={team2LifelineState.doubleSip.isActive}
                  isUsed={team2LifelineState.doubleSip.isUsed}
                  icon={<SportsBar fontSize={isMobile ? "small" : "large"} />}
                />
              </MotionBox>
            </AnimatePresence>
            <AnimatePresence>
              <MotionBox
                key={`team2-doubleUp-${team2LifelineState.doubleUp.isActive}-${team2LifelineState.doubleUp.isUsed}`}
                animate={
                  team2LifelineState.doubleUp.isUsed
                    ? "used"
                    : team2LifelineState.doubleUp.isActive
                    ? "active"
                    : "initial"
                }
                variants={lifelineVariants}
              >
                <LifelineButton
                  isActive={team2LifelineState.doubleUp.isActive}
                  isUsed={team2LifelineState.doubleUp.isUsed}
                  icon={
                    <LooksTwoRounded fontSize={isMobile ? "small" : "large"} />
                  }
                />
              </MotionBox>
            </AnimatePresence>
            <AnimatePresence>
              <MotionBox
                key={`team2-phoneAFriend-${team2LifelineState.phoneAFriend.isActive}-${team2LifelineState.phoneAFriend.isUsed}`}
                animate={
                  team2LifelineState.phoneAFriend.isUsed
                    ? "used"
                    : team2LifelineState.phoneAFriend.isActive
                    ? "active"
                    : "initial"
                }
                variants={lifelineVariants}
              >
                <LifelineButton
                  isActive={team2LifelineState.phoneAFriend.isActive}
                  isUsed={team2LifelineState.phoneAFriend.isUsed}
                  icon={<Phone fontSize={isMobile ? "small" : "large"} />}
                />
              </MotionBox>
            </AnimatePresence>
          </Box>
        </Box>
      </MotionBox>
    </MotionPaper>
  );
};

export default Scoreboard;
